import { AccountType } from '@autoriz/shared/accounts'

export type EntryType = 'feature' | 'improvement' | 'fix'

/** Type filter used by the changelog page tabs. */
export type EntryFilter = EntryType | 'all'

/** The architect's office: the architect and their collaborators. */
export const ARCHITECT_SIDE: readonly AccountType[] = [
  AccountType.ARCHITECT,
  AccountType.COLLABORATOR,
]

/** The commercial side: salesmen and the companies that employ them. */
export const COMMERCIAL_SIDE: readonly AccountType[] = [
  AccountType.SALESMAN,
  AccountType.SOCIETY,
]

/** Everyone who works dossiers — both sides, but not the end client. */
export const STAFF: readonly AccountType[] = [...ARCHITECT_SIDE, ...COMMERCIAL_SIDE]

/** Every account type, the end client included. Use only when it is true. */
export const EVERYONE: readonly AccountType[] = Object.values(AccountType)

export interface ChangelogEntry {
  type: EntryType
  title: string
  scope: string
  /** Plain-text body, rendered as a paragraph. */
  body: string
  /**
   * Account types this entry concerns — the only ones that see it, in the
   * welcome modal and on the page. Required and non-empty: there is no
   * "everyone by default", because an entry nobody scoped is how architect
   * tooling ended up in a commercial's "what's new". Pick from
   * {@link ARCHITECT_SIDE}, {@link COMMERCIAL_SIDE}, {@link STAFF},
   * {@link EVERYONE}, or list the roles.
   */
  roles: readonly AccountType[]
  /** Optional screenshot shown under the body. Path served from /public. */
  image?: string
  /** Alt text for {@link image}. */
  imageAlt?: string
}

/** A release's title and hero summary, as one audience reads them. */
export interface Headline {
  title: string
  summary?: string
}

export interface Release {
  version: string
  /** Title for a reader who sees every entry of the release. */
  title: string
  date: string
  /** One-line hero summary shown in the welcome modal. */
  summary?: string
  /**
   * Title/summary for a role that sees only part of the release. Without one,
   * such a role reads its first entry's title and no summary — never a
   * headline that advertises what it cannot see.
   */
  headlines?: Partial<Record<AccountType, Headline>>
  entries: ChangelogEntry[]
}

export const TYPE_LABEL: Record<EntryType, string> = {
  feature: 'Nouveau',
  improvement: 'Amélioration',
  fix: 'Correction',
}

export const releases: Release[] = [
  {
    version: '2.21.0',
    title: 'Plusieurs accès pour une entreprise',
    date: '2 novembre 2026',
    summary:
      "Une entreprise partenaire peut avoir plusieurs accès à Autoriz, chacun avec sa propre adresse e-mail.",
    headlines: {
      [AccountType.ARCHITECT]: {
        title: 'Plusieurs accès pour une entreprise',
        summary:
          "Donnez un accès à chaque personne d'une entreprise partenaire : direction, secrétariat, comptabilité.",
      },
      [AccountType.COLLABORATOR]: {
        title: 'Plusieurs accès pour une entreprise',
        summary:
          "Donnez un accès à chaque personne d'une entreprise partenaire : direction, secrétariat, comptabilité.",
      },
      [AccountType.SALESMAN]: {
        title: "Le client de l'étude repris dans le projet",
        summary:
          "Le nom de l'étude se saisit en civilité, nom et prénom, et le projet reprend ce client quand l'étude devient un projet.",
      },
      [AccountType.SOCIETY]: {
        title: 'Vos collègues peuvent avoir leur propre accès',
        summary:
          "Chaque personne de votre entreprise peut se connecter avec sa propre adresse e-mail.",
      },
    },
    entries: [
      {
        type: 'feature',
        title: "Plusieurs accès pour une entreprise",
        scope: 'entreprises',
        roles: ARCHITECT_SIDE,
        body: "Sur la fiche d'une entreprise, la carte « Accès à Autoriz » liste toutes les personnes qui se connectent pour elle, et « Ajouter un accès » en crée un de plus à partir d'une adresse e-mail. Chaque accès reçoit son propre lien pour définir son mot de passe, et se désactive sans toucher aux autres. Tous les accès ont les mêmes droits sur l'espace de l'entreprise, et chacun reçoit les e-mails de suivi des dossiers.",
      },
      {
        type: 'feature',
        title: 'Vos collègues peuvent avoir leur propre accès',
        scope: 'compte',
        roles: [AccountType.SOCIETY],
        body: "Votre direction, votre secrétariat ou votre comptabilité peuvent maintenant se connecter à Autoriz chacun avec sa propre adresse e-mail et son propre mot de passe. Ils voient les mêmes dossiers, études et commerciaux que vous, et reçoivent les mêmes e-mails de suivi. Demandez à votre architecte d'ajouter les adresses de vos collègues.",
      },
      {
        type: 'improvement',
        title: 'Clients, commerciaux et entreprises : des listes plus lisibles',
        scope: 'gestion',
        roles: ARCHITECT_SIDE,
        body: "Les pages « Mes clients », « Mes commerciaux » et « Mes entreprises » passent au même affichage que l'espace des commerciaux : une carte par fiche, des filtres en un clic avec leur nombre, et une recherche qui trouve un nom, une ville, une adresse e-mail ou un téléphone. Chaque commercial affiche son entreprise et l'état de son compte, chaque entreprise ses accès à Autoriz et son nombre de commerciaux. Cliquez sur une carte pour ouvrir la fiche.",
      },
      {
        type: 'improvement',
        title: 'La création d’un projet change de présentation',
        scope: 'projets',
        roles: ARCHITECT_SIDE,
        body: "La création d'un projet reprend le même affichage que l'espace commercial, en moins d'étapes : le type de projet, puis le commercial (choisi après un filtre par entreprise), le client et l'adresse sur un seul écran, les parcelles, et enfin l'étude urbaine à rattacher s'il y en a une. Depuis une étude, l'adresse et les parcelles restent visibles et modifiables.",
      },
      {
        type: 'improvement',
        title: 'La création d’une étude urbaine change de présentation',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "La création d'une étude reprend le même affichage que la création d'un projet, une étape par sujet : le nom de l'étude, l'adresse, les parcelles, puis le demandeur et le commercial (choisi après un filtre par entreprise). Un récapitulatif s'affiche avant de créer l'étude.",
      },
      {
        type: 'improvement',
        title: '« Mes projets » change de visage',
        scope: 'projets',
        roles: ARCHITECT_SIDE,
        body: "La liste de vos projets range ses vues dans une colonne à gauche : « Action requise » (À contrôler, À traiter, et la nouvelle vue « Fin d'instruction proche », qui réunit les dossiers déposés sans réponse de la mairie dont l'instruction se termine dans les 15 jours), « En cours » et « Archives », chacune avec son nombre. Les projets abandonnés ont maintenant leur propre vue et n'apparaissent plus dans « Tous », sauf si vous cochez « Afficher les abandonnés ». Le tableau est plus compact : la ville sous le nom du client, l'entreprise sous le commercial, une icône pour le type de projet et pour son auteur (la légende est sous le tableau), une étiquette de couleur pour l'état. Filtrez par commercial, entreprise ou type depuis la barre de recherche, et cliquez n'importe où sur une ligne pour ouvrir le projet. La colonne se replie en icônes sur un petit écran.",
      },
      {
        type: 'improvement',
        title: "L'ajout d'une entreprise prend le même style que sa fiche",
        scope: 'entreprises',
        roles: [AccountType.ARCHITECT],
        body: "La page « Nouvelle entreprise » reprend les cartes de la fiche d'une entreprise et s'enregistre depuis la barre fixée en bas de l'écran, comme l'ajout d'un client ou d'un commercial. Les champs obligatoires sont signalés avant l'envoi, une couleur de suivi est proposée d'office et reste modifiable, et un message vous prévient si l'entreprise n'a pas pu être créée. Ses accès à Autoriz, ses droits et son catalogue se règlent ensuite depuis sa fiche.",
      },
      {
        type: 'fix',
        title: 'Les parcelles du cadastre se mettent de nouveau à jour',
        scope: 'cadastre',
        roles: ARCHITECT_SIDE,
        body: "Depuis le 1er septembre, la mise à jour des départements du cadastre échouait : Autoriz ne trouvait plus de date dans la liste des publications du cadastre national, et les parcelles n'étaient plus rafraîchies. La mise à jour mensuelle et l'import d'un département depuis Outils › « Départements Cadastre » récupèrent de nouveau la dernière publication.",
      },
      {
        type: 'improvement',
        title: "Le nom de l'étude : la civilité, le nom et le prénom du client",
        scope: 'étude urbaine',
        roles: COMMERCIAL_SIDE,
        body: "La première étape de la demande d'étude ne cherche plus un client existant, ce qui laissait croire qu'il fallait une fiche client. Vous choisissez la civilité, puis saisissez le nom et le prénom du client : ils forment le nom de l'étude, par exemple « M. DUPONT Éric », que l'étape vous montre avant de continuer. Le client n'a besoin ni de compte ni de fiche ; sa fiche se crée quand l'étude devient un projet.",
      },
      {
        type: 'improvement',
        title: "Le client de l'étude repris dans le projet",
        scope: 'étude urbaine · projets',
        roles: STAFF,
        body: "Quand une étude devient un projet, son nom (« M. DUPONT Éric ») donne le client : s'il fait déjà partie de vos clients, il devient le premier déclarant sans rien chercher ; sinon, la fiche d'un nouveau particulier s'ouvre, déjà remplie de la civilité, du nom et du prénom. Si plusieurs de vos clients portent ce nom, la recherche s'ouvre sur eux pour que vous choisissiez le bon.",
      },
      {
        type: 'improvement',
        title: "« Transformer en projet » sur une étude validée",
        scope: 'étude urbaine · projets',
        roles: ARCHITECT_SIDE,
        body: "Le bouton « Transformer en projet » n'apparaît plus sur une étude en cours : il s'affiche en haut de l'étude une fois qu'elle est validée.",
      },
    ],
  },
  {
    version: '2.20.0',
    title: 'La bonne zone dans les grands PLU intercommunaux',
    date: '29 octobre 2026',
    summary:
      "L'IA lit les pages du secteur de la parcelle, et l'e-mail de fin d'analyse vous dit quand les dispositions générales manquent.",
    entries: [
      {
        type: 'improvement',
        title: "L'IA lit le chapitre du secteur de la parcelle, pas tout le règlement",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Dans un PLU intercommunal découpé en dizaines de secteurs (« UCA2.1.3 » pour un tissu mixte dense de Tourcoing), l'IA s'appuie sur la liste des zones publiée au Géoportail de l'urbanisme pour trouver le chapitre du secteur de la parcelle, et ne lit que ses pages. Elle ne cite plus la règle d'un secteur voisin, et l'analyse va beaucoup plus vite. Une zone qui couvrirait plus de la moitié du règlement n'est plus crue : ses réponses restent à vérifier.",
      },
      {
        type: 'feature',
        title: "L'e-mail de fin d'analyse signale les dispositions générales manquantes",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand l'IA n'a pas trouvé les dispositions générales du PLU, souvent une pièce à part (« Livre I – Dispositions générales ») absente du Géoportail, l'e-mail de fin d'analyse vous le dit. Ajoutez cette pièce aux documents de l'étude, avec « dispositions générales » dans son nom, puis relancez l'analyse : davantage de réponses pourront être données comme sûres.",
      },
      {
        type: 'fix',
        title: 'Les règlements écrits en tableau sont lus article par article',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand un règlement présente ses règles dans un tableau (Article, Sous-article, Règle), comme celui de la Métropole européenne de Lille, l'IA rattachait parfois une règle à l'article voisin : une distance aux limites séparatives pouvait être lue comme une distance à la voie. Chaque ligne du tableau reste maintenant sous son article.",
      },
      {
        type: 'fix',
        title: "Plus de « Non règlementé » à côté d'une valeur",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand l'IA trouve une valeur pour une question, ou pour une question qui en dépend (par exemple « toiture terrasse » pour le nombre de pans), elle ne propose plus en même temps « Non règlementé », qui la contredisait.",
      },
      {
        type: 'improvement',
        title: "Une valeur absente du formulaire prend la plus proche",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand le règlement fixe une valeur que le formulaire ne propose pas, l'IA coche la valeur la plus proche pour un minimum (une pente minimale de 32° devient « minimum 30° », un minimum de 4,5 m devient « minimum 5 m »). Un maximum ne dépasse jamais la valeur du règlement : 7,5 m devient « 7 m maximum ». Si la valeur la plus proche est trop éloignée, l'IA ne coche rien. La réponse reste à vérifier, avec la phrase du règlement qui donne la valeur exacte.",
      },
    ],
  },
  {
    version: '2.19.0',
    title: "L'IA lit le règlement du PLU et propose les réponses de l'étude",
    date: '25 octobre 2026',
    summary:
      "Les réponses de l'étude proposées avec leur citation et leur fiabilité, le règlement relu deux fois, et des dispositions générales lues en entier.",
    headlines: {
      [AccountType.ARCHITECT]: {
        title: "L'IA lit le règlement du PLU et propose les réponses de l'étude",
        summary:
          "Les réponses de l'étude proposées avec leur citation et leur fiabilité, le règlement relu deux fois, et chaque notification désactivable depuis son e-mail.",
      },
      [AccountType.COLLABORATOR]: {
        title: "L'IA lit le règlement du PLU et propose les réponses de l'étude",
        summary:
          "Les réponses de l'étude proposées avec leur citation et leur fiabilité, le règlement relu deux fois, et des dispositions générales lues en entier.",
      },
      [AccountType.SALESMAN]: {
        title: 'Des e-mails plus rares, plus souriants, et un compte à rebours',
        summary:
          "Les rappels se font plus discrets, les e-mails changent de ton, et chaque notification se désactive depuis l'e-mail.",
      },
      [AccountType.SOCIETY]: {
        title: 'Des e-mails plus rares, plus souriants, et un compte à rebours',
        summary:
          "Les rappels se font plus discrets, les e-mails changent de ton, et chaque notification se désactive depuis l'e-mail.",
      },
      [AccountType.CLIENT]: {
        title: 'Votre adresse e-mail, avec ou sans majuscules',
        summary:
          'La connexion reconnaît votre adresse e-mail quelle que soit la façon dont vous la tapez.',
      },
    },
    entries: [
      {
        type: 'feature',
        title: "Une question et des documents pour l'architecte, dans votre demande d'étude",
        scope: 'étude urbaine',
        roles: COMMERCIAL_SIDE,
        body: "La demande d'étude se termine par une étape facultative, « Précisions » : vous y écrivez votre question ou le contexte du terrain (« véranda existante non cadastrée, quel recul sur voie ? ») et vous joignez des photos ou des PDF. Plus besoin d'un e-mail à côté : l'architecte les trouve dans l'étude. Vous pouvez compléter votre demande plus tard, depuis la page de l'étude, tant qu'elle n'est pas validée : modifier la question, ajouter un document, retirer ceux que vous avez ajoutés. Les documents que l'architecte partage avec vous s'y affichent aussi. Rien de tout cela n'apparaît dans le rapport ni chez votre client.",
      },
      {
        type: 'feature',
        title: 'La demande du commercial dans l’étude, et des documents partagés avec lui',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Au-dessus du questionnaire, « Demande du commercial » affiche la question qu'il a posée en demandant l'étude, et le nombre de documents partagés : un clic ouvre l'onglet « Documents » sur ce nouveau type. Vous pouvez y ajouter vos propres documents (un croquis du recul, un plan annoté) : le commercial les voit sur la page de son étude. Dans la liste des études, une icône signale une question, une autre les documents partagés. Quand le commercial complète une demande déjà envoyée, un seul e-mail vous prévient, quelques minutes après son dernier ajout ; cette alerte se désactive dans vos préférences. Ces documents n'apparaissent ni dans le rapport ni chez le client.",
      },
      {
        type: 'feature',
        title: 'Transformer une étude en projet depuis sa page',
        scope: 'étude urbaine · projets',
        roles: ARCHITECT_SIDE,
        body: "Une étude autonome se transforme en projet depuis sa page : « Transformer en projet », en haut de l'étude, ouvre la création du projet avec l'adresse, les parcelles et le commercial de l'étude. Il reste à choisir le type de projet et le client. L'étude est rattachée au projet sans changer d'état : une étude acceptée le reste, alors qu'elle revenait jusqu'ici à l'état « Modifiée ».",
      },
      {
        type: 'feature',
        title: 'Qui a créé le projet',
        scope: 'projets',
        roles: ARCHITECT_SIDE,
        body: "La liste des projets a une colonne « Créé par » : Architecte ou Collaborateur pour les projets créés au cabinet, Commercial ou Entreprise pour ceux ouverts par le côté commercial, chacun dans sa couleur. Le filtre de la colonne ne garde que les uns ou les autres. Dans le suivi du projet, « Création du projet » nomme de nouveau son auteur, l'entreprise par son nom. Les projets plus anciens dont l'auteur n'avait pas été enregistré affichent un tiret.",
      },
      {
        type: 'fix',
        title: '« Transformer en projet » reprend votre étude',
        scope: 'études urbaines · projets',
        roles: COMMERCIAL_SIDE,
        body: "« Transformer en projet » ouvrait une demande de projet vide, et l'étude n'était pas rattachée au projet créé : il fallait tout ressaisir, et le dossier attendait une nouvelle étude. La demande reprend désormais l'adresse et les parcelles de l'étude et rappelle le nom du client qu'elle porte. Ce nom n'est pas une fiche client : un client qui n'a encore jamais eu de projet n'apparaît pas dans la recherche, et la demande vous invite à le créer. À la création, l'étude est rattachée au projet.",
      },
      {
        type: 'fix',
        title: "Quand votre architecte crée vos projets",
        scope: 'études urbaines',
        roles: COMMERCIAL_SIDE,
        body: "Pour une société dont l'architecte crée les projets, une étude livrée proposait « Transformer en projet », qui menait à un refus. La page de l'étude invite désormais à demander à votre architecte de la transformer en projet.",
      },
      {
        type: 'fix',
        title: 'Votre adresse e-mail, avec ou sans majuscules',
        scope: 'connexion',
        roles: EVERYONE,
        body: "Une adresse e-mail enregistrée avec des majuscules ne se reconnaissait qu'écrite à l'identique : un compte enregistré « CONTACT@… » ne se connectait pas en tapant « contact@… », et le mot de passe, pourtant juste, était dit incorrect. La connexion, le lien de connexion et le mot de passe oublié reconnaissent désormais votre adresse quelles que soient les majuscules, et les nouvelles adresses sont enregistrées en minuscules.",
      },
      {
        type: 'fix',
        title: 'Les étiquettes de couleur de nouveau en couleur',
        scope: 'listes',
        roles: ARCHITECT_SIDE,
        body: "Les étiquettes colorées des listes s'affichaient toutes en gris : le type d'une étude (« Autonome » en vert, « Projet » en bleu), l'état des factures, les parcelles. Elles retrouvent leurs couleurs.",
      },
      {
        type: 'improvement',
        title: 'Les études autonomes repérables au premier coup d’œil',
        scope: 'étude urbaine',
        roles: COMMERCIAL_SIDE,
        body: 'Dans la liste de vos études urbaines, une étude autonome porte désormais un badge violet « Étude autonome », distinct du badge gris des études liées à un projet.',
      },
      {
        type: 'fix',
        title: "Le règlement du PLU de nouveau rattaché à la création d'une étude",
        scope: 'étude urbaine · documents',
        roles: ARCHITECT_SIDE,
        body: "Le Géoportail de l'urbanisme a changé l'adresse de ses fichiers, et la création d'une étude ne rattachait plus le règlement du PLU ni ses autres pièces écrites : il fallait les déposer à la main. Ils sont de nouveau téléchargés automatiquement, avec la parcelle et le zonage.",
      },
      {
        type: 'feature',
        title: "L'IA propose les réponses de l'étude, avec leur citation",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand le règlement du PLU d'une étude est rattaché, l'IA le lit après la collecte des données et remplit le formulaire. Un bandeau en tête du formulaire compte les réponses qu'elle a proposées et celles qui restent à vérifier. Sous chaque réponse proposée, sa fiabilité (fiable, à vérifier ou douteuse) et la phrase du règlement sur laquelle elle repose, avec son article et sa page. « Voir dans le PDF » ouvre le règlement à cette page et surligne le passage cité. Vérifiez, et corrigez au besoin en changeant la valeur : la réponse devient la vôtre. « Analyser le PLU », dans le menu « ⋯ » en haut de l'étude, relance la lecture ; vos propres réponses ne sont jamais remplacées.",
      },
      {
        type: 'improvement',
        title: 'Le règlement relu une seconde fois avant de proposer une réponse comme fiable',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "L'IA relit désormais le règlement une seconde fois avant de remplir le formulaire. Une réponse n'est proposée comme fiable que si les deux lectures la trouvent ; sinon, elle arrive « à vérifier ». L'analyse dure quelques minutes de plus, et le bandeau d'attente compte aussi les sections de la relecture.",
      },
      {
        type: 'fix',
        title: "Les dispositions générales d'un long règlement lues en entier",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand les dispositions générales d'un PLU dépassent une cinquantaine de pages, l'IA les lit en plusieurs morceaux. Elle perdait une partie des articles en les rassemblant : sur un PLU intercommunal, les règles de plantations, de stationnement, des matériaux et des couleurs communes à toutes les zones n'étaient jamais lues. L'IA répondait alors « Non règlementé » en citant un article sans rapport. Elle les lit désormais en entier, et y cherche ce que l'article de la zone renvoie aux « dispositions communes ».",
      },
      {
        type: 'improvement',
        title: "« À vérifier » quand le règlement a pu être mal découpé",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Avant de répondre, l'IA découpe le règlement en zones et en articles. Quand ce découpage est incertain (un chapitre retenu pour une zone voisine, des articles mal séparés, une part du chapitre qu'aucun article ne lit, des dispositions générales introuvables, une page dont le texte a dû être lu par reconnaissance optique), les réponses qui en dépendent ne sont plus proposées comme fiables : elles arrivent « à vérifier », même quand la phrase citée semble les établir.",
      },
      {
        type: 'fix',
        title: "Une analyse du PLU qui ne finissait jamais",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Sur certains règlements, la lecture des titres d'articles pouvait tourner sans fin, et l'analyse du PLU restait « en cours » sans jamais remplir le formulaire. Elle s'arrête désormais au bout de quelques secondes sur ces titres, et l'analyse va à son terme.",
      },
      {
        type: 'improvement',
        title: 'Les chapitres des dispositions générales proposés',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Pour la question « dispositions générales », l'IA propose, à vérifier, chaque chapitre que le règlement porte réellement (lotissement, PPRI, adaptations mineures, portée respective du règlement), en citant l'intitulé ou la phrase où il est nommé.",
      },
      {
        type: 'fix',
        title: "La règle d'une autre commune n'est plus prise pour la vôtre",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Dans un PLU intercommunal, une phrase qui commence par « Sur les communes de … » ou « Dans la commune de … » ne vaut que pour ces communes. L'IA l'appliquait parfois à toutes : des tuiles imposées à trois villages devenaient la règle de l'étude. Elle l'écarte désormais quand la commune de la parcelle n'y figure pas. L'IA lit aussi mieux certaines règles écrites par cas (un abri de jardin, une liste d'interdictions, des distances données sous un sous-titre numéroté).",
      },
      {
        type: 'fix',
        title: '« Voir les dispositions communes » ne bloque plus la réponse',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand l'article de la zone renvoie aux dispositions communes, l'IA les lit aussi. Si elles ne prescrivent rien sur le sujet, elle répond « Non règlementé » en citant l'article de la zone et les dispositions lues, au lieu de laisser la question vide.",
      },
      {
        type: 'fix',
        title: 'Les longues listes et les distances par cas lues en entier',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Une longue liste d'interdictions (« Sont interdits : … ») n'était lue qu'à moitié : l'IA la lit désormais jusqu'au bout. Et des distances données cas par cas (« 3 mètres si les façades sont aveugles, 8 mètres si l'une a des vues, 16 mètres si les deux en ont ») sont maintenant proposées toutes les trois, au lieu d'aucune.",
      },
      {
        type: 'fix',
        title: 'Les hauteurs par bande, et un nombre qui mesure autre chose',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand un tableau donne une hauteur dans la bande de 25 mètres et une autre au-delà, l'IA propose maintenant les deux, sans prendre la hauteur à l'acrotère pour celle du faîtage. Et un nombre qui mesure autre chose que la question (« un plan de façade de plus de 3 m de profondeur ») n'est plus proposé comme une distance de recul.",
      },
      {
        type: 'fix',
        title: 'Une distance « si baies » et sa distance « dans le cas contraire »',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand le règlement impose une distance si la façade a des baies et une autre « dans le cas contraire » (« 5 m si l'un ou l'autre comporte des baies, 3 m dans le cas contraire »), l'IA propose maintenant les deux, puisque l'étude ne dit pas si le projet aura des baies. Elle n'écarte plus non plus une telle distance à cause d'une phrase qui parle du recul par rapport à la rue.",
      },
      {
        type: 'fix',
        title: "Une règle n'est plus écartée par l'exception d'un autre cas",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "L'IA écartait parfois une réponse juste parce qu'une autre phrase de l'article semblait la contredire, alors que cette phrase valait pour un autre cas : un autre secteur, une toiture terrasse, un exemple chiffré, un autre élément de la clôture. Elle lit désormais à quel cas s'applique la phrase avant de l'opposer à la réponse ; ces réponses reviennent au formulaire.",
      },
      {
        type: 'fix',
        title: 'Un élément de liste relu sous la bonne liste',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand un règlement répète le même élément dans plusieurs listes (« à un minimum de 3 m dans le cas contraire », sous les venelles puis sous les limites séparatives), l'IA le vérifie maintenant sous la liste qui correspond à la question, et la réponse n'est plus laissée à vérifier pour rien.",
      },
      {
        type: 'fix',
        title: 'Les dispositions communes lues au bon endroit',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Dans un PLU intercommunal, l'IA lisait parfois l'article d'un élément protégé (« Mur de clôture protégé », « Espace boisé classé ») pour une règle de toute la zone ; elle lit désormais l'article général. Elle trouve aussi la hauteur des clôtures écrite dans une sous-partie de l'article de zone, et lit en entier un chapitre des dispositions communes découpé en plusieurs morceaux (le nombre d'arbres à planter). Enfin, quand un article réserve sa suite « aux autres communes », elle ne l'applique plus à la commune de la parcelle que l'article a nommée avant.",
      },
      {
        type: 'fix',
        title: "La règle de la commune de la parcelle, dans un PLU qui en liste plusieurs",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Un PLU intercommunal écrit souvent une règle pour quelques communes nommées (« Dans les zones UBb de Fontainebleau, Avon… ») et une autre « pour les autres communes ». L'IA prenait parfois la première liste pour la règle de toute la zone et proposait une hauteur qui ne valait pas pour la commune de la parcelle. Elle lit désormais à quelles communes chaque règle s'applique, et ne propose que celle de la commune de la parcelle.",
      },
      {
        type: 'fix',
        title: "« Non règlementé » n'est plus refusé sur une phrase hors sujet",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Pour répondre « Non règlementé », l'IA vérifie qu'aucune phrase du règlement ne fixe la règle. Elle se laissait arrêter par des phrases qui parlaient d'autre chose : une règle propre à un espace vert protégé, qui ne vaut que dans ce périmètre, ou des « servitudes » d'urbanisme lues comme une servitude de stationnement. Ces phrases ne l'arrêtent plus : la réponse est proposée comme fiable, avec les articles où l'IA a cherché.",
      },
      {
        type: 'fix',
        title: "L'IA lit le chapitre du secteur de la parcelle",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Certains PLU intercommunaux consacrent un chapitre à chaque secteur d'une zone (« Secteur UBa », « Secteur UBb »…), avec des règles différentes. L'IA lisait parfois le premier secteur au lieu de celui de la parcelle, et proposait les règles d'un autre secteur. Elle lit désormais le chapitre du secteur où se trouve la parcelle. Quand la zone de l'étude couvre plusieurs secteurs et que le zonage de la parcelle ne permet pas de choisir, l'analyse s'arrête au lieu de deviner : précisez le secteur dans la zone de l'étude, puis relancez « Analyser le PLU ».",
      },
      {
        type: 'improvement',
        title: "Une règle absente du PLU dit où l'IA a cherché",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand l'IA répond qu'une règle n'existe pas dans le PLU (« sans objet », « non règlementé »), elle ne peut citer aucune phrase du règlement. Elle indique désormais où elle a cherché : l'article qui ne dit rien sur le sujet, ou le sommaire de la zone quand aucun article n'en traite. Cette indication s'affiche « Lieu fouillé », sans guillemets, et « Voir dans le PDF » ouvre l'article concerné.",
      },
      {
        type: 'fix',
        title: "L'IA lit les mots que le règlement dessine au lieu de les écrire",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Certains règlements de PLU dessinent une partie de leurs mots au lieu de les écrire : la page les affiche, mais le texte du fichier ne les contient pas. L'IA lisait alors « limitée à 7 m et à 10 m au faîtage » là où le règlement dit « limitée à 7 m à l'égout du toit ou à l'acrotère et à 10 m au faîtage », et répondait « Non règlementé » à la hauteur à l'égout. Ces pages sont désormais repérées et relues comme une image, mot à mot. Les surfaces y restent des surfaces : « 600 m² » n'est plus lu comme une longueur de 600 m.",
      },
      {
        type: 'fix',
        title: "Les essences de végétation lues même quand le règlement parle d'« espèces »",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Un règlement qui interdit les « espèces invasives » ou recommande une liste d'« espèces locales » parle des essences de végétation, sans jamais employer ce mot. L'IA les lit désormais comme telles. Elle ne répond plus non plus « Non règlementé » en s'appuyant sur un simple intertitre du règlement, sans relire l'article qui suit et qui, lui, fixe la règle.",
      },
      {
        type: 'improvement',
        title: '« Voir dans le PDF » ouvre la bonne page',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Sous une réponse proposée par l'IA, « Voir dans le PDF » ouvre le règlement à la page de la phrase citée et la surligne. Sur un règlement intercommunal, qui répète les mêmes règles d'une zone à l'autre, la page manquait le plus souvent, et la phrase était cherchée dans tout le document. Elle est désormais trouvée sur les pages de l'article cité, y compris quand la citation s'étend sur plusieurs paragraphes, comme une liste sous son introduction.",
      },
      {
        type: 'improvement',
        title: "L'IA ne propose plus les règles écrites pour d'autres constructions que la maison",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Une étude porte toujours sur une maison individuelle : son extension, sa véranda, sa pergola ou sa piscine. L'IA proposait parfois une règle écrite pour un autre cas, par exemple le nombre de places de stationnement prévu pour les logements des gendarmes, ou le recul imposé aux commerces. Elle écarte désormais les règles chiffrées des logements collectifs ou aidés, des logements de fonction, des résidences avec services, des opérations de plusieurs logements, de l'habitat léger ou mobile et des autres destinations (commerces, bureaux…). Une question qui porte elle-même sur l'un de ces cas, comme l'interdiction des terrains de camping ou les destinations autorisées dans la zone, reste répondue.",
      },
      {
        type: 'improvement',
        title: 'Vos décisions de revue sont conservées',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Accepter une citation proposée par l'IA, la refuser avec son motif ou marquer une réponse comme validée n'était gardé que le temps de la visite. Ces décisions sont désormais enregistrées et vous les retrouvez à votre retour sur l'étude. Avec les propositions de l'IA, qui sont gardées même quand vous les corrigez, elles serviront à améliorer l'analyse du PLU.",
      },
      {
        type: 'feature',
        title: "Pendant l'analyse du PLU, le temps restant ; à la fin, un e-mail",
        scope: 'étude urbaine · e-mails',
        roles: ARCHITECT_SIDE,
        body: "La lecture du règlement par l'IA prend de dix à vingt minutes, selon ce que règle la zone. Pendant ce temps, le formulaire de l'étude passe en lecture seule sous un encadré qui indique l'heure de fin estimée et la section en cours de lecture. L'estimation tient compte du temps habituel de chaque section et se corrige au rythme du règlement, y compris pendant une section longue. À la fin, le formulaire se recharge de lui-même avec les réponses proposées, sans avoir à rafraîchir la page. L'architecte reçoit aussi un e-mail qui donne le nombre de réponses proposées et de réponses douteuses, ou qui signale que l'analyse s'est interrompue et peut être relancée. Cet e-mail se désactive dans « Mon compte → Notifications par e-mail ».",
      },
      {
        type: 'feature',
        title: "Un e-mail quand l'analyse du PLU attend votre intervention",
        scope: 'étude urbaine · e-mails',
        roles: ARCHITECT_SIDE,
        body: "À la création d'une étude, l'IA lance seule l'analyse du PLU quand la collecte des données a trouvé la zone et le règlement. Quand elle n'y arrive pas (une parcelle à cheval sur deux zones, aucun règlement au Géoportail, une collecte qui n'aboutit pas), vous recevez désormais un e-mail qui dit pourquoi et quoi faire : valider le zonage ou ajouter le règlement, puis lancer l'analyse depuis l'étude. Il suit le réglage « Analyse du PLU terminée » de votre compte.",
      },
      {
        type: 'improvement',
        title: 'La zone du PLU trouvée quand la parcelle touche une zone voisine',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "À la création d'une étude, la zone du PLU est posée automatiquement, puis l'IA lit le règlement. Une parcelle qui ne faisait que toucher une zone voisine par son bord était tenue pour à cheval sur les deux : il fallait valider le zonage à la main, puis lancer l'analyse. La zone retenue est désormais celle qu'affiche la fiche de l'étude ; seule une zone voisine qui couvre au moins 5 % de la parcelle vous laisse le choix.",
      },
      {
        type: 'feature',
        title: "Reprendre les réponses d'une étude déjà validée",
        scope: 'étude urbaine',
        roles: [AccountType.ARCHITECT],
        body: "Quand une étude validée porte déjà sur le même PLU et la même zone, une nouvelle étude reprend ses réponses. Si la zone a été trouvée automatiquement à la création de l'étude, ses réponses sont reprises dès la collecte des données, et l'IA ne relit pas le règlement. Si vous avez choisi la zone vous-même, l'étude vous propose la reprise au-dessus du questionnaire : « Reprendre les réponses » remplace les réponses de l'étude par les siennes. Dans les deux cas, il ne vous reste qu'à vérifier les réponses, puis à valider l'étude, et un bandeau rappelle de quelle étude elles viennent, avec sa date de validation. Le même PLU se reconnaît au document du Géoportail de l'Urbanisme quand les deux études le connaissent, sinon à la commune. Une règle retirée depuis n'est pas reprise, et les citations restent à faire.",
      },
      {
        type: 'fix',
        title: 'Le rapport du lundi arrive enfin',
        scope: 'e-mails · assistant',
        roles: [AccountType.ARCHITECT],
        body: "Le rapport du lundi matin, qui rassemble les dossiers urgents de l'assistant et la raison de chacun, n'était jamais parti : il échouait avant de trouver ses destinataires. Il vous parvient désormais chaque lundi, dès qu'au moins un dossier est urgent.",
      },
      {
        type: 'improvement',
        title: 'Des e-mails plus rares, plus souriants, et un compte à rebours',
        scope: 'e-mails · rappels',
        roles: COMMERCIAL_SIDE,
        body: "Les rappels se font plus discrets : celui du numéro de dépôt n'arrive qu'un mois après le dépôt et revient au plus une fois par mois, un dossier en création ou des documents à faire signer ne sont rappelés qu'au bout de trois semaines, puis toutes les deux semaines, et un dossier à corriger ou un type à valider tous les dix jours. Sur un dossier déposé, le rappel ne compte plus les jours écoulés depuis le dépôt mais ceux qui restent avant la réponse de la mairie. Les e-mails changent aussi de ton : « Bonne nouvelle, votre dossier est accepté ! », « C'est prêt ! », « C'est parti, le dossier est déposé ». Enfin, chaque e-mail porte en bas un lien « Désactiver cette notification » (et chaque rappel un lien « Ne plus recevoir ce rappel ») : un clic, une confirmation, et vous ne le recevez plus. Vous pouvez le réactiver à tout moment depuis « Mon compte ».",
      },
      {
        type: 'improvement',
        title: 'Désactiver une notification depuis l’e-mail',
        scope: 'e-mails · rappels',
        roles: [AccountType.ARCHITECT],
        body: "Chaque e-mail de notification porte en bas un lien « Désactiver cette notification », et chaque rappel du récapitulatif quotidien un lien « Ne plus recevoir ce rappel » : un clic, une confirmation, et vous ne le recevez plus — réactivable à tout moment depuis « Mon compte ». Le rappel de récépissé manquant n'arrive plus qu'au bout de deux semaines, et le rappel du numéro de dépôt indique les jours restants avant la réponse de la mairie plutôt que ceux écoulés depuis le dépôt.",
      },
      {
        type: 'improvement',
        title: "Demande d'étude urbaine : la bonne commune, et un délai suivi",
        scope: 'étude urbaine',
        roles: COMMERCIAL_SIDE,
        body: "La recherche d'adresse signale les propositions situées dans une autre commune que celle tapée, et dit quand elle ne trouve rien. Chaque étude en cours affiche sa date de livraison estimée (5 jours ouvrés) et passe en rouge une fois en retard. La dernière étape récapitule client, adresse et parcelles avant l'envoi, le client peut être repris de vos fiches, et l'envoi est confirmé à l'arrivée. Le titre de la liste suit l'onglet affiché.",
      },
    ],
  },
  {
    version: '2.18.0',
    title: 'Des nouveautés à votre mesure, et des corrections sur le suivi des dossiers',
    date: '27 septembre 2026',
    summary:
      "Chaque compte ne voit plus que les nouveautés qui le concernent ; les études se citent valeur par valeur, et la reprise d'un dossier refusé enregistre enfin chaque correction.",
    headlines: {
      [AccountType.ARCHITECT]: {
        title: 'Citer une réponse valeur par valeur, et un éditeur d\'étude allégé',
        summary:
          "Chaque valeur d'une réponse à choix multiple porte sa propre source, la commune d'une parcelle n'est plus devinée, et une section corrigée par le commercial revient à relire.",
      },
      [AccountType.COLLABORATOR]: {
        title: 'Citer une réponse valeur par valeur, et un éditeur d\'étude allégé',
        summary:
          "Chaque valeur d'une réponse à choix multiple porte sa propre source, la commune d'une parcelle n'est plus devinée, et une section corrigée par le commercial revient à relire.",
      },
      [AccountType.SALESMAN]: {
        title: 'Corriger un dossier refusé, sans perdre une saisie',
        summary:
          "Les dossiers à déposer passent dans « À faire », une section déjà validée se corrige pendant la reprise, et les nombres acceptent la virgule.",
      },
      [AccountType.SOCIETY]: {
        title: 'Corriger un dossier refusé, sans perdre une saisie',
        summary:
          "Les dossiers à déposer passent dans « À faire », une section déjà validée se corrige pendant la reprise, et les nombres acceptent la virgule.",
      },
    },
    entries: [
      {
        type: 'improvement',
        title: 'Seules les nouveautés qui vous concernent',
        scope: 'nouveautés',
        roles: STAFF,
        body: "La fenêtre des nouveautés et cette page ne montrent plus que ce qui touche votre type de compte : un commercial n'y lit plus les outils de l'architecte, ni l'architecte ceux du commercial. Si vous n'êtes pas venu depuis plusieurs mises à jour, la fenêtre rassemble tout ce qui vous concerne depuis votre dernière visite, plutôt que la seule dernière version. Elle se souvient de chaque compte séparément, même sur un ordinateur partagé.",
      },
      {
        type: 'fix',
        title: 'Plus de rappel de dépôt sur un dossier déjà accepté',
        scope: 'e-mails · rappels',
        roles: [AccountType.ARCHITECT, AccountType.SALESMAN, AccountType.SOCIETY],
        body: "Un dossier dont la réponse de la mairie venait d'être enregistrée pouvait encore recevoir, le lendemain, un rappel « Dépôt sans numéro », sans un mot sur l'acceptation. Les rappels liés au dépôt (numéro de dépôt, récépissé, délai d'instruction) s'arrêtent désormais dès que la réponse de la mairie est enregistrée. Le rappel sur le numéro s'appelle maintenant « Numéro de dépôt à saisir » et précise qu'aucune pièce ne manque : il reste seulement à reporter sur la plateforme le numéro indiqué sur le récépissé.",
      },
      {
        type: 'improvement',
        title: 'Un bouton « Enregistrer » sur chaque section du dossier',
        scope: 'dossier · saisie',
        roles: COMMERCIAL_SIDE,
        body: "Les sections du dossier (terrain, habitation existante, véranda, pergola, piscine) ne s'enregistrent plus toutes seules pendant que vous tapez, et ne se referment plus d'elles-mêmes dès le dernier champ rempli. Vous saisissez à votre rythme, puis le bouton « Enregistrer » en bas de la section enregistre tout d'un coup et la referme. Tant que ce n'est pas fait, « Modifications non enregistrées » vous le rappelle, et fermer la section vous demande d'abord confirmation pour ne rien perdre. Une section terminée que vous rouvrez montre bien ce que vous venez d'y saisir : elle paraissait vide jusqu'au rechargement de la page.",
      },
      {
        type: 'feature',
        title: 'Toutes les pièces du dossier en un seul téléchargement',
        scope: 'dossier · dépôt',
        roles: COMMERCIAL_SIDE,
        body: "Une fois le dossier envoyé par l'architecte, le bouton « Tout télécharger (.zip) », au-dessus des pièces du dossier, rassemble dans un seul fichier le formulaire Cerfa, la fiche des autres demandeurs quand le dossier en a une, et la matrice. Chaque pièce y est nommée par son rôle et numérotée dans l'ordre de signature : vous transmettez le dossier complet au client en une fois, plutôt que trois fichiers séparés.",
      },
      {
        type: 'feature',
        title: 'Une source par valeur sur une réponse à choix multiple',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Une réponse à plusieurs valeurs s'appuie souvent sur plusieurs phrases du règlement, et une seule citation ne pouvait pas dire laquelle justifiait quoi. Chaque valeur sélectionnée porte désormais sa propre source. Une valeur que vous désélectionnez garde la sienne, marquée « à revoir », que vous pouvez effacer. Les règles à réponse unique ne changent pas.",
      },
      {
        type: 'improvement',
        title: 'Des PLU volumineux acceptés',
        scope: 'étude urbaine · documents',
        roles: ARCHITECT_SIDE,
        body: "Un règlement de PLU, avec ses plans de zonage, dépasse souvent la taille qu'acceptait l'étude. Le PLU, le PPRI, le rapport Géorisques, la carte des monuments historiques, le plan cadastral et l'analyse urbaine se déposent désormais jusqu'à 300 Mo (en PDF). Un fichier lourd peut prendre plusieurs minutes à envoyer : il n'est plus refusé à l'arrivée parce que l'envoi a duré trop longtemps.",
      },
      {
        type: 'improvement',
        title: "Un pied d'éditeur d'étude plus compact",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Dans l'éditeur d'étude V2, le pied du panneau de gauche tient sur une ligne : demandeur, date de création et de dernière modification, sous l'état de sauvegarde. Le changement de demandeur passe dans le menu « ⋯ » de l'en-tête, et le bouton « Étude urbaine » du pied disparaît, puisqu'il doublait « Télécharger ».",
      },
      {
        type: 'fix',
        title: 'La note « Lecture seule » ne coupe plus la valeur',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Dans l'éditeur de nœud, les lignes « Synonymes / formulations » et « À ne pas retenir quand… » écrasaient leur valeur sous la note « Lecture seule » (« Aucune » s'affichait « Aucu / ne »). La note passe maintenant sous la valeur.",
      },
      {
        type: 'improvement',
        title: 'Une section corrigée par le commercial revient à relire',
        scope: 'dossier · validation',
        roles: ARCHITECT_SIDE,
        body: "Sur un dossier renvoyé en modification, le commercial peut désormais corriger une section que vous aviez déjà validée. La section repasse alors « à relire », comme toute section modifiée. C'est aussi le cas quand il change l'e-mail ou le mobile d'un déclarant, ou les coordonnées d'un signataire supplémentaire : ces modifications ne vous échappaient plus sans retirer la validation.",
      },
      {
        type: 'fix',
        title: "La commune d'une parcelle n'est plus devinée",
        scope: 'parcelles',
        roles: STAFF,
        body: "Une référence cadastrale comme « 000 AB 506 » existe dans des centaines de communes. La parcelle cliquée sur la carte pouvait recevoir la commune d'une autre, et donc le mauvais PLU. La carte transmet désormais la commune de la parcelle elle-même. Quand une parcelle arrive sans commune et que plusieurs correspondent, un message vous le dit au lieu d'en choisir une au hasard. Les parcelles déjà enregistrées ont été corrigées.",
      },
      {
        type: 'fix',
        title: "La recherche d'adresse ne plante plus",
        scope: 'création de dossier',
        roles: STAFF,
        body: "Quand le service national d'adresses répondait par une erreur, la recherche d'adresse de la création d'un dossier ou d'une étude échouait. Elle affiche maintenant simplement « aucun résultat », et vous pouvez réessayer.",
      },
      {
        type: 'improvement',
        title: 'Les dossiers à déposer en mairie sont dans « À faire »',
        scope: 'mes dossiers',
        roles: COMMERCIAL_SIDE,
        body: "Un dossier dont les documents vous ont été envoyés attend que vous le fassiez signer et le déposiez en mairie. Il était pourtant rangé dans « En attente de la mairie ». Il apparaît désormais dans « À faire », marqué « À déposer », avec le bouton « Déposer en mairie ». Il reste dans « En attente de la mairie » quand c'est l'architecte qui dépose en ligne.",
      },
      {
        type: 'improvement',
        title: 'Corriger une section déjà validée pendant la reprise',
        scope: 'dossier',
        roles: COMMERCIAL_SIDE,
        body: "Sur un dossier renvoyé en modification, une section que l'architecte avait validée, typiquement « Habitation existante », refusait vos corrections sans rien dire : la coche « enregistré » s'affichait, puis la valeur revenait. Vous pouvez maintenant la corriger ; elle repart chez l'architecte pour relecture. Si un enregistrement est refusé, un message le dit au lieu d'afficher la coche.",
      },
      {
        type: 'improvement',
        title: '« Validé » et « Complet », en vert',
        scope: 'dossier',
        roles: COMMERCIAL_SIDE,
        body: "La synthèse du dossier distingue maintenant une section que l'architecte a validée (« Validé », pastille pleine avec une coche) d'une section simplement remplie (« Complet »). Les deux s'affichent en vert, au lieu du gris et du noir.",
      },
      {
        type: 'fix',
        title: 'Les nombres acceptent la virgule, et le champ garde la main',
        scope: 'dossier',
        roles: COMMERCIAL_SIDE,
        body: "Taper « 12,5 » dans une surface enregistrait un champ vide, puis effaçait la saisie. Les champs numériques acceptent la virgule comme le point, et les espaces entre milliers. Le champ ne se bloque plus pendant qu'il s'enregistre, donc les chiffres tapés à ce moment-là ne se perdent plus. L'enregistrement se confirme par une coche verte.",
      },
    ],
  },
  {
    version: '2.17.0',
    title: 'Changer le commercial d\'un dossier, et classer le récépissé avec la réponse de la mairie',
    date: '23 septembre 2026',
    summary:
      "Un dossier ou une étude peut changer de commercial, et le commercial classe le numéro de dépôt et le récépissé dans le formulaire de réponse de la mairie.",
    headlines: {
      [AccountType.ARCHITECT]: {
        title: "Changer le commercial d'un dossier ou d'une étude",
        summary:
          "Un dossier se rattache à n'importe quel commercial, et ses études le suivent.",
      },
      [AccountType.COLLABORATOR]: {
        title: "Changer le commercial d'un dossier ou d'une étude",
        summary:
          "Un dossier se rattache à n'importe quel commercial, et ses études le suivent.",
      },
      [AccountType.SOCIETY]: {
        title: 'Confier un dossier à un autre commercial, et classer le récépissé avec la réponse de la mairie',
        summary:
          "Un dossier passe d'un de vos commerciaux à un autre, et le numéro de dépôt et le récépissé se classent avec la réponse de la mairie.",
      },
      [AccountType.SALESMAN]: {
        title: 'Le numéro de dépôt et le récépissé avec la réponse de la mairie',
        summary:
          'Le formulaire « La mairie a répondu ? » accueille le numéro de dépôt et le récépissé.',
      },
    },
    entries: [
      {
        type: 'feature',
        title: 'Changer le commercial d\'un dossier ou d\'une étude',
        scope: 'dossier',
        roles: ARCHITECT_SIDE,
        body: "Jusqu'ici, un dossier restait attaché au commercial pour qui il avait été ouvert, même quand ce commercial partait ou que le client était repris par un autre. Vous pouvez désormais le rattacher à n'importe quel commercial, quelle que soit son entreprise : depuis la carte « Commercial » de la synthèse, qui s'affiche aussi quand le dossier n'a pas de commercial, et depuis le menu « ⋯ » de l'éditeur d'étude urbaine. Les études du dossier changent de commercial avec lui. Une étude déjà validée le reste : changer de commercial ne la repasse pas en modification.",
      },
      {
        type: 'feature',
        title: 'Confier un dossier à un autre de vos commerciaux',
        scope: 'dossier',
        roles: [AccountType.SOCIETY],
        body: "Un commercial s'en va, un portefeuille est à rééquilibrer : depuis la page Informations d'un dossier ou la page d'une étude, vous pouvez rattacher le dossier à un autre commercial de votre entreprise. La liste ne propose que vos commerciaux. Les études du dossier suivent le dossier, et le nouveau commercial les retrouve dans son espace.",
      },
      {
        type: 'improvement',
        title: 'Le numéro de dépôt et le récépissé avec la réponse de la mairie',
        scope: 'dossier',
        roles: COMMERCIAL_SIDE,
        body: "Quand vous avez déposé le dossier vous-même, le formulaire « La mairie a répondu ? » de l'étape « Traitement en mairie » propose maintenant, à côté du commentaire, le numéro de dépôt, prérempli avec celui du dossier pour que vous n'ayez qu'à le corriger, et l'envoi du récépissé de dépôt. Le récépissé est enregistré dès que vous le déposez, et l'architecte le retrouve dans les documents du dossier. Les deux champs sont facultatifs : vous pouvez enregistrer la réponse sans eux, et un numéro laissé vide ne remplace pas celui déjà enregistré.",
      },
    ],
  },
  {
    version: '2.16.0',
    title: 'Le préambule et le glossaire que chaque étude imprime',
    date: '6 septembre 2026',
    summary:
      "Les règles d'urbanisme accueillent les textes que le cabinet écrit une fois et que toutes ses études impriment : le préambule qui clôt la première page, et les définitions qui ferment le rapport — propres à chaque type de document.",
    entries: [
      {
        type: 'feature',
        title: 'Un préambule et un glossaire par type de document, écrits une fois pour toutes les études',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Jusqu'ici, tout ce qu'une étude imprimait dépendait des réponses au questionnaire : le contenu d'un nœud et sa note de glossaire n'apparaissaient que si ce nœud avait été renseigné. Deux textes ne fonctionnent pas ainsi — le paragraphe qui introduit chaque étude, et les définitions que le cabinet explique systématiquement — et il n'existait aucun endroit où les écrire. Deux boutons, « Préambule » et « Glossaire », les ouvrent depuis la page des règles d'urbanisme : le préambule s'imprime après le tableau des parcelles, en clôture de la première page, et les définitions statiques ferment le rapport, à la suite de celles qu'apportent les règles répondues et sous le même titre « Glossaire ». Une définition est un titre et un contenu, écrits dans le même éditeur que le contenu des règles, et paraît dans l'ordre que vous fixez. Ces textes appartiennent au type de document sélectionné : un PLU, une carte communale et le RNU sont trois réglementations distinctes, et chacune a son préambule et ses définitions. Au passage, le rapport ne se termine plus par une page blanche.",
      },
    ],
  },
  {
    version: '2.15.0',
    title: 'Déplacer une règle sans la faire disparaître des études',
    date: '4 septembre 2026',
    summary:
      "Déplacer une règle enregistre désormais, dans les études qui y avaient répondu, les réponses nécessaires pour qu'elle continue d'y être posée.",
    entries: [
      {
        type: 'improvement',
        title: 'Les études suivent la règle déplacée',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Une règle n'est posée que si la réponse dont elle dépend a été donnée, et celle de sa question parente, et ainsi de suite. Déplacer une règle changeait donc en silence les études qui l'atteignaient : insérer un niveau au-dessus d'une branche — la raison même de la fonction — créait une question à laquelle aucune étude n'avait répondu, et toutes perdaient la branche d'un coup. Ce qu'elles avaient renseigné restait en base, mais n'était plus ni demandé ni imprimé. Le déplacement enregistre maintenant, dans les études qui avaient répondu à la branche déplacée, les réponses menant à sa nouvelle position ; la fenêtre « Déplacer » annonce combien d'études seront mises à jour avant de confirmer, et le confirme après. Sur une question à réponse unique, la réponse existante est remplacée par celle de la destination.",
      },
      {
        type: 'improvement',
        title: 'Une étude validée n\'est jamais réécrite',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Une étude validée a été livrée : le déplacement n'y touche pas. La fenêtre indique combien d'études validées sont concernées et prévient que la règle n'y sera plus posée, pour que la reprise se décide étude par étude plutôt que d'être faite à votre insu.",
      },
    ],
  },
  {
    version: '2.14.1',
    title: 'Les branches dont la réponse a été supprimée réapparaissent',
    date: '4 septembre 2026',
    summary:
      "Une règle rattachée à une réponse depuis supprimée n'était plus dessinée dans l'arbre, alors qu'elle continuait d'être posée dans les études.",
    entries: [
      {
        type: 'fix',
        title: "Une règle déplacée ne disparaît plus de l'arbre des nœuds",
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Quand une réponse était supprimée alors que des règles en dépendaient, ces règles quittaient silencieusement le graphe : elles restaient posées dans les études, mais plus rien ne les montrait ni ne permettait de les rouvrir. La fenêtre « Déplacer » proposait pourtant encore cette branche comme destination, si bien qu'une règle déplacée vers elle disparaissait à son tour, sans moyen de la récupérer. Ces branches sont désormais dessinées sous la règle à laquelle la réponse appartenait : le graphe montre tout ce que le questionnaire demande, et chaque destination proposée par « Déplacer » correspond à un nœud visible à l'écran.",
      },
    ],
  },
  {
    version: '2.14.0',
    title: 'Déplacer une règle plutôt que reconstruire la branche',
    date: '3 septembre 2026',
    summary:
      "Une règle du questionnaire peut être rattachée ailleurs dans l'arbre, avec tout ce qui en dépend : insérer un niveau ne demande plus de tout recréer à la main.",
    entries: [
      {
        type: 'feature',
        title: 'Déplacer une règle, avec ses sous-questions et ses contenus',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Ajouter un niveau au questionnaire obligeait à reconstruire une branche : pour placer une nouvelle question au-dessus de questions existantes, il fallait recréer chacune d'elles sous la nouvelle puis supprimer les originales — en perdant leur contenu rédigé, et en butant, pour une règle qu'une étude a déjà renseignée, sur le refus de suppression qui la protège. L'éditeur de nœud propose désormais « Déplacer » sur une règle : un arbre des sections et des réponses s'ouvre, vous choisissez la destination, et la règle s'y rattache avec toutes ses sous-questions et leurs contenus. Choisir une section en fait une question de premier niveau ; choisir une réponse la rend conditionnelle à cette réponse. L'emplacement actuel et la branche en cours de déplacement sont grisés, et un avertissement signale un déplacement qui change de section. Les réponses déjà enregistrées par les études sont conservées.",
      },
    ],
  },
  {
    version: '2.13.0',
    title: "Un glossaire en fin d'étude, sans dupliquer les règles",
    date: '3 septembre 2026',
    summary:
      "Une règle d'urbanisme peut désormais porter un renvoi de glossaire : le terme s'affiche à côté de la règle, sa définition dans le glossaire en fin d'étude.",
    entries: [
      {
        type: 'feature',
        title: 'Un glossaire sur une règle, sans créer de règle en double',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Pour faire figurer une définition dans une étude, il fallait jusqu'ici dupliquer la règle et écrire le texte dans le contenu de la copie — une question de plus dans le questionnaire, qui comptait en outre dans les règles à répondre et pouvait bloquer une validation. L'éditeur de contenu propose maintenant, à côté du contenu, un « Terme du renvoi » et un texte de glossaire : ils appartiennent au même nœud, n'ajoutent aucune question, et le graphe signale d'une étiquette « Glossaire » les nœuds qui en portent un. Le terme est obligatoire dès qu'une définition est écrite, puisque c'est lui qui sert de renvoi ; vider les deux champs supprime le renvoi.",
      },
      {
        type: 'improvement',
        title: 'Toutes les définitions réunies dans un glossaire, en fin d\'étude',
        scope: 'étude urbaine',
        roles: ARCHITECT_SIDE,
        body: "Dans l'étude, le terme apparaît en bleu italique à la suite de la règle concernée ; sa définition, elle, est imprimée dans un glossaire placé après la dernière règle, qui regroupe toutes les définitions du document dans l'ordre où le lecteur les rencontre. Les règles se lisent donc comme des règles, et les définitions se cherchent toujours au même endroit. Le renvoi est un lien : un clic depuis la règle amène directement à la définition, même plusieurs pages plus loin. Chaque définition est titrée par son terme, avec la même présentation encadrée qu'un titre de contenu.",
      },
    ],
  },
  {
    version: '2.12.0',
    title: 'Fini les doublons : un même document ne peut plus être envoyé deux fois',
    date: '7 août 2026',
    summary:
      "Le même fichier ne peut plus être ajouté deux fois à un dossier : un envoi en double est refusé, et le dossier garde une seule copie.",
    entries: [
      {
        type: 'improvement',
        title: 'Un même document ne peut plus être envoyé deux fois',
        scope: 'documents',
        roles: COMMERCIAL_SIDE,
        body: "Lors de l'envoi des pièces d'un dossier, un fichier déjà présent — le même contenu, qu'il soit renommé ou non — est désormais refusé, tout comme un fichier sélectionné deux fois dans le même envoi. Le message vous indique la pièce concernée et l'envoi est annulé, sans rien déposer, pour que le dossier ne se remplisse plus de copies identiques. Les fichiers réellement différents s'envoient normalement.",
      },
    ],
  },
  {
    version: '2.11.0',
    title: 'Alertes e-mail : ne plus perdre de temps sur un dossier oublié',
    date: '6 août 2026',
    summary:
      "Un e-mail quand un dossier vous attend — et un seul par jour, qui regroupe tout. Chacun choisit les alertes qu'il reçoit depuis « Mon compte ».",
    entries: [
      {
        type: 'feature',
        title: 'Être prévenu quand un dossier vous attend',
        scope: 'notifications',
        roles: STAFF,
        body: "Les situations où un dossier n'avance plus déclenchent désormais un e-mail : dossier renvoyé pour modification, type de dossier à valider, documents envoyés à la signature et toujours pas déposés, dossier en création depuis plus de 14 jours, dépôt sans numéro depuis 14 jours, récépissé de dépôt manquant, délai d'instruction écoulé. La décision de la mairie est également annoncée dès son enregistrement.",
      },
      {
        type: 'feature',
        title: 'Un seul e-mail par jour, qui regroupe tous vos dossiers',
        scope: 'notifications',
        roles: STAFF,
        body: "Les relances quotidiennes sont rassemblées dans un seul message, avec une section par situation et la liste des dossiers concernés. Une même situation n'est rappelée qu'une fois par semaine tant qu'elle dure : vingt dossiers en attente font un e-mail, pas vingt.",
      },
      {
        type: 'feature',
        title: 'Choisir les alertes que vous recevez',
        scope: 'mon compte · notifications',
        body: "Mon compte › « Notifications par e-mail » liste les alertes qui vous concernent, chacune avec un interrupteur indiquant si elle est active pour vous. Les entreprises y retrouvent les mêmes alertes que leurs commerciaux. L'architecte garde la main sur les réglages par défaut, par entreprise, depuis Outils › Notifications.",
        roles: STAFF,
      },
      {
        type: 'fix',
        title: 'Les préférences de notification s\'enregistrent',
        scope: 'mon compte · notifications',
        roles: COMMERCIAL_SIDE,
        body: "Modifier une préférence de notification échouait silencieusement pour les commerciaux et les entreprises. Le réglage est désormais correctement enregistré.",
      },
    ],
  },
  {
    version: '2.10.0',
    title: 'Ordre personnalisé des RAL, couleurs & matériaux',
    date: '16 juillet 2026',
    summary:
      'Rangez les RAL, couleurs et matériaux dans l\'ordre de votre choix — valeurs par défaut comme références propres à chaque entreprise — et retrouvez cet ordre partout où ces valeurs sont proposées.',
    entries: [
      {
        type: 'feature',
        title: 'Réorganiser les RAL, couleurs & matériaux par défaut',
        scope: 'outils · couleurs & matériaux',
        roles: ARCHITECT_SIDE,
        body: 'Dans Outils › « RAL, matériaux & couleurs par défaut », chaque valeur par défaut peut désormais être déplacée avec les flèches « Déplacer avant / après ». L\'ordre choisi est enregistré et repris partout où ces valeurs sont proposées — dans l\'éditeur des défauts comme dans les formulaires de projet (pergola, véranda, piscine) — à la place du classement alphabétique. Les nouvelles entrées s\'ajoutent à la fin de la liste, puis peuvent être déplacées.',
      },
      {
        type: 'feature',
        title: 'Fiche entreprise : uniquement vos références, et réorganisables',
        scope: 'entreprises · couleurs & matériaux',
        roles: ARCHITECT_SIDE,
        body: 'Sur la fiche d\'une entreprise, la section « RAL, matériaux & couleurs » n\'affiche désormais que les références propres à cette entreprise : les valeurs par défaut, gérées dans les Outils, n\'encombrent plus la liste (elles restent bien sûr proposées dans les formulaires de projet). Ces entrées personnalisées se réorganisent avec les mêmes flèches « Déplacer avant / après » que les valeurs par défaut, et l\'ordre choisi est enregistré — sans jamais modifier l\'ordre des valeurs par défaut.',
      },
      {
        type: 'fix',
        title: 'Ajout d\'une image sur une référence corrigé',
        scope: 'entreprises · outils · couleurs & matériaux',
        roles: ARCHITECT_SIDE,
        body: 'L\'ajout d\'une image à une teinte RAL, une couleur ou un matériau échouait avec une erreur au moment du téléversement, aussi bien dans l\'éditeur des valeurs par défaut que sur la fiche entreprise. Ces images se téléversent de nouveau normalement.',
      },
      {
        type: 'fix',
        title: 'Ajout d\'une plateforme de dépôt sans erreur',
        scope: 'outils · plateformes de dépôt',
        roles: ARCHITECT_SIDE,
        body: 'L\'enregistrement d\'une plateforme de dépôt dématérialisé échouait avec une erreur lorsque le lien ou les notes étaient laissés vides. Le formulaire — dans les Outils comme dans la synthèse du projet — accepte de nouveau un enregistrement dont ces champs optionnels sont vides.',
      },
    ],
  },
  {
    version: '2.9.0',
    title: 'Facturation des études urbaines',
    date: '17 juin 2026',
    summary:
      'Les études urbaines se facturent comme les autres produits : un produit « Étude urbaine » par entreprise (ou par défaut), une facture créée automatiquement à la demande, et le suivi dans la facturation globale.',
    entries: [
      {
        type: 'feature',
        title: 'Produit « Étude urbaine » et catalogue de produits par défaut',
        scope: 'outils · facturation · entreprises',
        roles: ARCHITECT_SIDE,
        body: 'L\'étude urbaine est désormais un produit facturable, au même titre que le permis de construire ou la déclaration préalable. Définissez son prix entreprise par entreprise depuis la fiche entreprise, ou une valeur par défaut partagée via le nouvel outil « Produits par défaut » (Outils › Produits par défaut). Ce catalogue par défaut s\'applique à toutes les entreprises qui n\'ont pas défini leur propre prix, et reprend la même interface que le catalogue par entreprise.',
      },
      {
        type: 'feature',
        title: 'Facturation automatique de l\'étude urbaine à la demande',
        scope: 'études urbaines · facturation',
        roles: STAFF,
        body: 'Lorsqu\'une étude urbaine est demandée — qu\'elle soit autonome ou rattachée à un projet — une facture est créée automatiquement à partir du produit « Étude urbaine » de l\'entreprise (ou du produit par défaut). Si ce produit est en « paiement à la commande », seul un architecte peut envoyer la demande : les autres rôles sont bloqués avec un message explicite. Pour un produit payable en fin de mois, la demande passe normalement.',
      },
      {
        type: 'improvement',
        title: 'Études urbaines dans la facturation globale',
        scope: 'facturation',
        roles: ARCHITECT_SIDE,
        body: 'La page de facturation globale gère désormais les factures d\'études urbaines à côté des factures de projet. Une colonne « Dossier » identifie chaque facture par son type (Projet ou Étude urbaine) avec un lien vers la fiche correspondante, et toutes les actions habituelles (marquer payé, modifier le prix, supprimer) fonctionnent à l\'identique.',
      },
      {
        type: 'fix',
        title: 'Libellés des produits signature et ABF',
        scope: 'facturation',
        roles: ARCHITECT_SIDE,
        body: 'Les produits « Signature 19-39 » et « Signature 39-60 » s\'affichaient avec leur identifiant technique, et « ABF » sans intitulé complet. Ils portent désormais un libellé clair (dont « Architecte des Bâtiments de France » pour ABF) partout où le catalogue de produits est affiché.',
      },
    ],
  },
  {
    version: '2.8.0',
    title: 'Contenu éditorial sur toutes les questions de l\'analyse urbaine',
    date: '15 juin 2026',
    summary:
      'Ajoutez titres et descriptions à n\'importe quelle question — pas seulement aux feuilles — et mettez en forme vos titres (couleur, alignement, taille). Tout apparaît dans le PDF, dans l\'ordre logique de l\'arbre.',
    entries: [
      {
        type: 'feature',
        title: 'Du contenu sur n\'importe quelle question, pas seulement les feuilles',
        scope: 'analyse urbaine · éditeur · PDF',
        roles: ARCHITECT_SIDE,
        body: 'Jusqu\'ici, un titre et une description ne pouvaient être attachés qu\'aux questions terminales (les « feuilles ») de l\'arbre de règles. Vous pouvez désormais ajouter du contenu à n\'importe quelle question — y compris les questions parentes — pour une valeur de réponse donnée, via le même bouton « Éditer le contenu » qu\'auparavant. Dans le PDF de l\'étude urbaine, ces contenus apparaissent dans l\'ordre logique de l\'arbre : le texte d\'une question parente précède celui de ses sous-questions, et chaque bloc n\'apparaît qu\'une seule fois.',
      },
      {
        type: 'feature',
        title: 'Mise en forme des titres de contenu (couleur, alignement, taille)',
        scope: 'analyse urbaine · éditeur · PDF',
        roles: ARCHITECT_SIDE,
        body: 'Chaque titre de contenu peut désormais être mis en forme : couleur, alignement (gauche, centré, droite) et taille, choisis parmi une palette prédéfinie. La mise en forme est enregistrée par bloc et restituée fidèlement dans le PDF, le titre occupant toute la largeur de la page pour que l\'alignement soit respecté. Les titres sans mise en forme conservent l\'apparence par défaut (rouge, centré).',
      },
    ],
  },
  {
    version: '2.7.0',
    title: 'Indentation dynamique des sous-questions dans l\'analyse urbaine v2',
    date: '10 juin 2026',
    entries: [
      {
        type: 'feature',
        title: 'Sous-questions indentées selon leur profondeur réelle (UAv2)',
        scope: 'analyse urbaine · formulaire · v2',
        roles: ARCHITECT_SIDE,
        body: 'Dans le formulaire d\'analyse urbaine version 2, les sous-questions conditionnelles sont maintenant décalées selon leur profondeur réelle dans la chaîne de dépendances. Chaque niveau dispose d\'une couleur d\'accent distincte (bleu, vert, doré, violet…) pour que la hiérarchie des questions soit lisible d\'un coup d\'œil, même sur les cascades à 5 ou 6 niveaux. La présentation est purement visuelle : l\'ordre des questions et le PDF généré ne changent pas.',
      },
      {
        type: 'improvement',
        title: 'Tooltip explicatif sur le bouton « Continuer sans réserves »',
        scope: 'projets · validation',
        roles: ARCHITECT_SIDE,
        body: 'Le bouton « Continuer sans réserves » indique désormais pourquoi il est inactif. Passez la souris dessus pour voir la liste des points bloquants : étude urbaine manquante ou non validée, formulaires non vérifiés, formulaire refusé à corriger ou documents obligatoires absents. Plus besoin de chercher — vous savez en un coup d\'oeil ce qu\'il reste à faire avant de valider le projet.',
      },
    ],
  },
  {
    version: '2.6.0',
    title: 'Dossiers piscine allégés des pièces non applicables',
    date: '8 juin 2026',
    entries: [
      {
        type: 'feature',
        title: 'Dossiers piscine sans vues de dessin ni relevé de l’existant',
        scope: 'projets · dossiers · piscine',
        roles: ARCHITECT_SIDE,
        body: 'Les dossiers de type Piscine ne demandent plus les pièces propres à un volume bâti : vues orthogonales (dessus, droite, gauche, face), insertion 3D et relevé de l’existant sont retirés de la liste des documents requis. Les photos de propriété et le mandat client restent obligatoires.',
      },
      {
        type: 'feature',
        title: 'Pivoter les images dans l’éditeur des règles d’analyse urbaine',
        scope: 'analyse urbaine · éditeur · PDF',
        roles: ARCHITECT_SIDE,
        body: 'Les images insérées dans les règles d’analyse urbaine peuvent désormais être pivotées directement dans l’éditeur, par pas de 90°, grâce aux boutons ↺ et ↻ qui apparaissent dans la barre flottante lors de la sélection d’une image. La rotation est enregistrée dans le contenu et restituée fidèlement dans le PDF généré.',
      },
    ],
  },
  {
    version: '2.5.0',
    title: 'Validation du type de projet par le commercial',
    date: '6 juin 2026',
    entries: [
      {
        type: 'feature',
        title: 'Modale de validation du type de projet (commercial)',
        scope: 'hub · projets · commercial',
        roles: COMMERCIAL_SIDE,
        body: 'À l’étape 3 du suivi projet, les commerciaux peuvent désormais consulter et valider le type d’autorisation retenu par l’architecte (DP, PC ou permis modificatif) avant de démarrer la rédaction du dossier. La modale présente le type choisi avec ses options (ABF, signature d’architecte) et les notes laissées par l’architecte. En l’absence d’étude urbaine, un avertissement signale le risque de refus en mairie. Le commercial peut valider pour lancer la rédaction, ou renvoyer le projet en modification.',
      },
    ],
  },
  {
    version: '2.4.0',
    title: 'Hub projets repensé, statut étude urbaine & nouvelle liste clients',
    date: '5 juin 2026',
    entries: [
      {
        type: 'feature',
        title: 'Nouvelle liste clients en vue hub',
        scope: 'clients · commerciaux',
        roles: COMMERCIAL_SIDE,
        body: 'La liste des clients affiche désormais une interface hub moderne pour les comptes commerciaux et société — grille avec avatars, recherche textuelle intelligente, filtres par type (particulier / société) et pagination. L’ancienne vue reste disponible pour les autres types de compte.',
      },
      {
        type: 'improvement',
        title: 'Boutons d’action colorés selon l’état du projet',
        scope: 'hub · projets',
        roles: COMMERCIAL_SIDE,
        body: 'Sur votre liste de projets, le bouton d’action de chaque carte prend désormais la couleur de l’étape en cours : orange pour un dossier à compléter, rouge pour un dossier refusé à corriger, vert pour un projet à valider. Un regard suffit pour savoir quoi faire ensuite.',
      },
      {
        type: 'feature',
        title: 'Puce de statut étude urbaine sur chaque projet',
        scope: 'hub · étude urbaine',
        roles: COMMERCIAL_SIDE,
        body: 'Une puce colorée apparaît sur chaque carte projet pour indiquer l’avancement de l’étude urbaine : orange si aucune étude n’a encore été demandée, bleu si elle est en cours, vert une fois livrée, rouge si le projet est trop avancé pour en commander une.',
      },
      {
        type: 'fix',
        title: 'Salutation avec le vrai prénom du compte',
        scope: 'hub · authentification',
        roles: COMMERCIAL_SIDE,
        body: 'Le message d’accueil affichait un nom vide ou générique. Il utilise désormais le nom réel associé au compte connecté, récupéré depuis le même cache que l’en-tête.',
      },
    ],
  },
  {
    version: '2.3.0',
    title: 'Éditeur des RAL, matériaux & couleurs par défaut',
    date: '29 mai 2026',
    entries: [
      {
        type: 'feature',
        title: 'Gérer les RAL, matériaux & couleurs par défaut',
        scope: 'outils · références',
        roles: ARCHITECT_SIDE,
        body: 'Un nouvel outil permet de gérer les teintes RAL, matériaux et couleurs proposés par défaut à toutes les entreprises — titre, description et image. Vous retrouvez la même interface que l’éditeur par entreprise, mais les valeurs modifiées ici sont partagées partout. Chaque entreprise peut toujours ajouter ses propres variantes depuis sa fiche.',
      },
    ],
  },
  {
    version: '2.2.0',
    title: 'Plateformes de dépôt dématérialisé par commune',
    date: '28 mai 2026',
    entries: [
      {
        type: 'feature',
        title: 'Plateformes de dépôt dématérialisé par commune',
        scope: 'outils · projets',
        roles: STAFF,
        body: 'Retrouvez le lien vers la plateforme de dépôt dématérialisé de chaque commune directement depuis l’onglet Outils, et consultez-le sur la synthèse de vos projets. Les architectes peuvent compléter ou corriger le lien et les notes en un clic — l’information est partagée pour toutes les demandes de la commune.',
      },
    ],
  },
  {
    version: '2.1.2',
    title: 'Références par entreprise & documents requis selon le contexte',
    date: '21 mai 2026',
    entries: [
      {
        type: 'feature',
        title: 'Bibliothèques de références par entreprise',
        scope: 'références · entreprises',
        roles: ARCHITECT_SIDE,
        body: 'Chaque entreprise gère désormais ses propres teintes RAL, matériaux et couleurs. Vos entrées personnalisées apparaissent en tête des listes déroulantes, avant les valeurs par défaut.',
        image: '/changelog/references-par-entreprise.png',
        imageAlt:
          'Catalogue RAL, matériaux et couleurs personnalisable par entreprise',
      },
      {
        type: 'feature',
        title: 'Documents et champs requis selon le contexte',
        scope: 'projets · validation',
        roles: STAFF,
        body: 'Les pièces et les champs de formulaire obligatoires s’adaptent à l’étape du projet, à son type et au type de compte. La validation de section signale précisément ce qu’il reste à compléter.',
        image: '/changelog/documents-requis.png',
        imageAlt:
          'Synthèse projet listant les formulaires et documents requis manquants',
      },
      {
        type: 'improvement',
        title: 'Recherche couleurs et matériaux plus tolérante',
        scope: 'références',
        roles: ARCHITECT_SIDE,
        body: 'Le sélecteur de couleurs et matériaux ignore désormais les accents et la casse — « gris anthracite » se trouve aussi bien que « Gris Anthracité ».',
      },
      {
        type: 'fix',
        title: 'Téléchargement des fichiers pour les comptes société',
        scope: 'permissions · société',
        roles: [AccountType.SOCIETY],
        body: 'Les comptes société ne pouvaient pas télécharger certains fichiers de projet. L’accès est désormais résolu correctement via leur entreprise rattachée.',
      },
    ],
  },
]

/** Numeric semver comparison: negative when `a` is older than `b`. */
export const compareVersions = (a: string, b: string): number => {
  const pa = a.split('.').map((n) => Number.parseInt(n, 10) || 0)
  const pb = b.split('.').map((n) => Number.parseInt(n, 10) || 0)
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (d !== 0) return d
  }
  return 0
}

/** The newest authored release — what build.env's APP_VERSION names. */
export const newestRelease = (): Release => releases[0]

/** Only the listed account types see an entry; an unknown role sees none. */
export const isEntryVisibleToRole = (
  entry: ChangelogEntry,
  role: string | undefined,
): boolean => !!role && entry.roles.includes(role as AccountType)

/** Entries of a release visible to `role`, optionally narrowed by type. */
export const visibleEntries = (
  release: Release,
  role: string | undefined,
  typeFilter: EntryFilter = 'all',
): ChangelogEntry[] =>
  release.entries.filter(
    (e) =>
      isEntryVisibleToRole(e, role) &&
      (typeFilter === 'all' || e.type === typeFilter),
  )

/** Releases that have at least one entry visible to `role`, newest first. */
export const visibleReleases = (role: string | undefined): Release[] =>
  releases.filter((r) => visibleEntries(r, role).length > 0)

/**
 * The release title and summary `role` reads. A role that sees every entry
 * reads the release's own; a role that sees part of it reads its override in
 * {@link Release.headlines}, else its first visible entry's title.
 */
export const releaseHeadline = (
  release: Release,
  role: string | undefined,
): Headline => {
  const override = role ? release.headlines?.[role as AccountType] : undefined
  if (override) return override
  const visible = visibleEntries(release, role)
  if (visible.length === release.entries.length) {
    return { title: release.title, summary: release.summary }
  }
  return { title: visible[0]?.title ?? release.title }
}

/**
 * Releases `role` has not been shown yet, newest first: every visible release
 * newer than `lastSeen`. With no `lastSeen` (a first visit) only the newest
 * release counts — nobody is greeted with the whole history.
 */
export const unseenReleases = (
  role: string | undefined,
  lastSeen: string | null,
): Release[] =>
  visibleReleases(role).filter((r) =>
    lastSeen === null
      ? r.version === newestRelease().version
      : compareVersions(r.version, lastSeen) > 0,
  )
